import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { LiffShell } from '@/components/liff/LiffShell'
import { toast } from '@/components/common/Toast'
import { Button, Input, Label, Textarea } from '@/components/ui'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import { useCreateExpense, useExpenseList } from '@/modules/expense/services/expense'
import { formatPrice } from '@/utils/number'

export const Route = createFileRoute('/liff/expense')({
  component: LiffExpensePage,
})

const CATEGORY_OPTIONS = [
  { key: 'cat_general', label: 'ทั่วไป' },
  { key: 'cat_shipping', label: 'ค่าขนส่ง' },
  { key: 'cat_office', label: 'วัสดุสำนักงาน' },
  { key: 'cat_labor', label: 'ค่าแรง / ช่าง' },
  { key: 'cat_other', label: 'อื่นๆ' },
] as const

const LEGACY_CATEGORY_TO_KEY: Record<string, string> = {
  ทั่วไป: 'cat_general',
  ค่าขนส่ง: 'cat_shipping',
  ค่าวัสดุสำนักงาน: 'cat_office',
  'ค่าแรง / ช่าง': 'cat_labor',
  อื่นๆ: 'cat_other',
}

function categoryLabel(stored: string): string {
  const key = LEGACY_CATEGORY_TO_KEY[stored] ?? stored
  return CATEGORY_OPTIONS.find((c) => c.key === key)?.label ?? stored
}

function LiffExpensePage() {
  const [expenseDate, setExpenseDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [category, setCategory] = useState<string>('cat_general')
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [location, setLocation] = useState<BillLocationValue>(BILL_LOCATION_VALUES[0])

  const listParams = useMemo(
    () => ({ page: 1, limit: 15, location }),
    [location],
  )
  const { data, refetch } = useExpenseList(listParams)
  const $create = useCreateExpense()

  const rows = data?.data ?? []

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const n = parseFloat(amount)
    if (Number.isNaN(n) || n <= 0) {
      toast.toastFailed('กรุณาระบุจำนวนเงินที่ถูกต้อง')
      return
    }
    try {
      await $create.mutateAsync({
        expense_date: expenseDate,
        category,
        amount: n,
        note: note.trim() || undefined,
        location,
      })
      toast.toastSuccess('บันทึกค่าใช้จ่ายเรียบร้อย')
      setAmount('')
      setNote('')
      refetch()
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined
      toast.toastFailed(msg ?? 'บันทึกไม่สำเร็จ')
    }
  }

  return (
    <LiffShell title="บันทึกค่าใช้จ่าย">
      <form onSubmit={onSubmit} className="mx-auto max-w-md space-y-5">
        <Field label="วันที่">
          <Input
            type="date"
            className="h-12 text-base"
            value={expenseDate}
            onChange={(e) => setExpenseDate(e.target.value)}
            required
          />
        </Field>

        <Field label="สาขา">
          <Select value={location} onValueChange={(v) => setLocation(v as BillLocationValue)}>
            <SelectTrigger className="h-12 text-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BILL_LOCATION_VALUES.map((loc) => (
                <SelectItem key={loc} value={loc} className="text-base py-3">
                  {BILL_LOCATION_LABELS[loc]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="หมวดหมู่">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-12 text-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((c) => (
                <SelectItem key={c.key} value={c.key} className="text-base py-3">
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="จำนวนเงิน (บาท)">
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="1"
            className="h-12 text-lg font-semibold"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            required
          />
        </Field>

        <Field label="หมายเหตุ (ไม่บังคับ)">
          <Textarea
            className="min-h-[80px] text-base"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="รายละเอียดเพิ่มเติม"
          />
        </Field>

        <Button
          type="submit"
          className="h-14 w-full text-lg font-semibold"
          disabled={$create.isLoading}
        >
          {$create.isLoading ? 'กำลังบันทึก...' : 'บันทึกค่าใช้จ่าย'}
        </Button>
      </form>

      <section className="mx-auto mt-8 max-w-md">
        <h2 className="mb-3 text-base font-semibold text-slate-700">รายการล่าสุด</h2>
        {rows.length === 0 ? (
          <p className="rounded-xl bg-white px-4 py-6 text-center text-slate-500">ยังไม่มีรายการ</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li
                key={row.id}
                className="rounded-xl border border-border/50 bg-white px-4 py-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-slate-800">{categoryLabel(row.category)}</span>
                  <span className="shrink-0 text-lg font-bold text-blue-700">
                    {formatPrice(row.amount)} ฿
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-500">{row.expense_date}</p>
                {row.note ? (
                  <p className="mt-1 text-sm text-slate-600 line-clamp-2">{row.note}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </LiffShell>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-base font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  )
}

